import { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_IMPORT_ROUTE, PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { IMPORT_TYPE_PASS_HOLDER } from '@pages/events/utils/import-process-utils';

interface UseBulkProcessNavigationProps {
  eventId: string;
  importType: string;
  importSuccess: boolean;
  importData: { importId: string } | undefined;
}

export const useBulkProcessNavigation = ({
  eventId,
  importType,
  importSuccess,
  importData,
}: UseBulkProcessNavigationProps) => {
  const navigate = useNavigate();
  const { toggleModal, setPreventExit } = useContext(ModalContext);
  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);

  const closeModal = useCallback(() => {
    setPreventExit(false);
    toggleModal({ isOpen: false, type: '' });
  }, [toggleModal, setPreventExit]);

  const createProcessPath = useCallback(
    (importId: string) => {
      if (!importId) return;

      // For pass holders, use company-level route since passes are company-level
      if (importType === IMPORT_TYPE_PASS_HOLDER) {
        return COMPANY_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
          .replace(':clubSlug', clubSlug)
          .replace(':importId', importId)
          .replace(':importType', importType);
      }

      // For event-level imports, require eventId
      if (!eventId) return;
      return PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importId)
        .replace(':importType', importType);
    },
    [companyLegacyId, clubSlug, eventId, importType]
  );

  const navigateToBulkProcess = useCallback(
    (importId: string) => {
      const path = createProcessPath(importId);
      if (path) navigate(path);
      setPreventExit(false);
      closeModal();
    },
    [createProcessPath, navigate, setPreventExit, closeModal]
  );

  // Handle automatic navigation on import success
  useEffect(() => {
    if (importSuccess && importData?.importId) {
      navigateToBulkProcess(importData.importId);
    }
  }, [importSuccess, importData, navigateToBulkProcess]);

  return {
    closeModal,
    navigateToBulkProcess,
    createProcessPath,
  };
};
